import {
  Box,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FullTaskLayout from "../../Layouts/FullTask";
import FullTaskTopbar from "../../Components/Topbars/FullTaskTopbar";
import FullTaskSplit from "../../Layouts/FullTask/FullTaskSplit";
import { useNavigate, useParams } from "react-router";

import { routePaths } from "../../Router/router";
import { assetService, cacheService, orderService, setupService } from "../../Core/Api/services";
import GlobalConst from "../../Core/Utils/Constants/globals";

import { ModalContext } from "../../Components/Modals";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { UpdateMetadataFieldsRequest } from "../../Core/Api/Types/update-metadata-fields-request";
import ModalCreatePackage, {
  ModalPackageCreateOrEditElementProps,
} from "../../Components/Modals/ModalCreatePackage/modal-create-package";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import GenerativeFormBundleMaker from "./generative-form-bundlemaker";
import BundleMakerSubbar from "../../Components/Subbars/bundlemakerSubbar";
import LeftPanelBundleMaker from "./left-panel-bundlemaker";
import {
  BundleMetadataValidation,
  BundleMetadataValidationStatus,
  checkValidationByCountry,
  isMetadataLocalized,
} from "../../Utils/Metadata/utils-metadata-validation";
import RequiredIndicator from "../../Components/Misc/RequiredIndicator";
import { useDispatch, useSelector } from "react-redux";
import {
  AddMetadata,
  AddTitleMetadata,
  CancelEdition,
  ChangePackageName,
  GetArtworkMeta,
  GetBroadcasterList,
  GetBundleId,
  GetCulturesList,
  GetEditTitle,
  GetIsRightPanelOpen,
  GetMetadata,
  GetMetadataMap,
  GetOpenTitleId,
  GetPackageCultures,
  GetPackageList,
  GetSelectedCultureIndex,
  GetSelectedEpisode,
  GetSelectedPackageIndex,
  GetSelectedTitle,
  GetStatusMap,
  GetTitleCultures,
  GetTitleLocalizedMeta,
  GetTitleSelectedCulture,
  HandleCreatedPackage,
  InitBundleMaker,
  RemoveCulture,
  SetArtworkContentId,
  SetArtworks,
  SetBroadcasters,
  SetBundleId,
  SetCultures,
  SetCurrentPackageStatus,
  SetEditTitle,
  SetMetadata,
  SetMetadataMap,
  SetPackageBroadcasterId,
  SetPackageCultures,
  SetPackageList,
  SetSelectedCultureIndex,
  SetSelectedPackageIndex,
  SetSelectedTitle,
  SetStatusMap,
  SetTitleCultures,
  SetTitleLocalizedMeta,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import RightPanelBundleMaker from "./right-panel-bundlemaker";
import {
  getBundlesContent,
  getGeneratedName,
  getMetadataCultures,
  getArtworksContent,
  getTitleAssetCultures,
  getEpisodeMetadata,
} from "../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { UserHasAcl, UserHasLicence } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { Licences } from "../../Core/Api/Types/licences";
import { ObjectType } from "../../Core/Api/Types/object-type";
import EditTitle from "./edit-title-bundlemaker";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import SaveIcon from "@mui/icons-material/Save";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { ObjectStatus } from "../../Core/Api/Types/object-status";
import { CreateNewDbMetadataValue, GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { TitleStatus } from "../../Core/Api/Types/title-status";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { useTranslation } from "react-i18next";

const BundleMaker: React.FC = (props) => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  //------Select store values------

  const selectedPackageIndex = useSelector(GetSelectedPackageIndex);
  const packageList = useSelector(GetPackageList);
  const packageCultures = useSelector(GetPackageCultures);
  const broadcasters = useSelector(GetBroadcasterList);
  const title = useSelector(GetSelectedTitle);
  const bundleId = useSelector(GetBundleId);
  const cultures = useSelector(GetCulturesList);
  const openTab = useSelector(GetSelectedCultureIndex);
  const meta = useSelector(GetMetadata);
  const metaMap = useSelector(GetMetadataMap);
  const editTitle = useSelector(GetEditTitle);
  const isRightPanelOpen = useSelector(GetIsRightPanelOpen);
  const titleLocalizedMeta = useSelector(GetTitleLocalizedMeta);
  const titleCultures = useSelector(GetTitleCultures);
  const titleSelectedCulture = useSelector(GetTitleSelectedCulture);
  const statusMap = useSelector(GetStatusMap);
  const openTitleId = useSelector(GetOpenTitleId);
  const selectedCultureIndex = useSelector(GetSelectedCultureIndex);
  const artworkMeta = useSelector(GetArtworkMeta);
  const selectedEpisode = useSelector(GetSelectedEpisode);

  //------local state------

  const [isLoading, setIsLoading] = useState(false);
  const [objectFullName, setObjectFullName] = useState(GlobalConst.LOADING_TEXT);
  const [isSaving, setIsSaving] = useState(false);
  const [metadataListFilled, setMetadataListFilled] = useState<IDbBaseMetadataValueInfos[]>([]);
  const [titleStatus, setTitleStatus] = useState<TitleStatus>(TitleStatus.Pending);
  const [titleStatusMeta, setTitleStatusMeta] = useState<DbMetadataFieldInfos>(new DbMetadataFieldInfos());
  const [allMandatoryMetasFilled, setAllMandatoryMetasFilled] = useState<boolean>(false);
  const noTouchStatuses = [TitleStatus.Completed, TitleStatus.Validated, TitleStatus.Cancelled];

  const id: number = parseInt(params.id ?? "-1");
  const packageId = useMemo(
    () => (packageList[selectedPackageIndex] ? packageList[selectedPackageIndex].id : -1),
    [packageList, selectedPackageIndex]
  );

  //------Memoized values derived from state------

  const selectedCultureId = useMemo(() => packageCultures[openTab]?.id, [packageCultures, openTab]);
  const packageBroadcasterId = useMemo(
    () =>
      packageList && packageList.length >= selectedPackageIndex + 1 && selectedPackageIndex >= 0
        ? (packageList[selectedPackageIndex].metadata?.find(
            (x) => x.id === cacheService.GetMetadata().find((y) => y.tagName === "broadcasterId")?.id
          )?.value as number)
        : undefined,
    [selectedPackageIndex, packageList.length]
  );

  const packageEpisodeNumber = useMemo(
    () =>
      packageList && packageList.length >= selectedPackageIndex + 1 && selectedPackageIndex >= 0
        ? (packageList[selectedPackageIndex].metadata?.find(
            (x) => x.id === cacheService.GetMetadata().find((y) => y.tagName === "serieEpisodeNumberProduction")?.id
          )?.value as number)
        : null,
    [selectedPackageIndex, packageList.length]
  );

  const subBarTitle = useMemo(
    () =>
      editTitle
        ? t("page:bundleMakerSubar.displayedTitle.culture", { count: titleCultures.length })
        : selectedPackageIndex >= 0 && packageList.length > selectedPackageIndex
        ? packageList[selectedPackageIndex]?.name
        : t("page:bundleMakerSubar.displayedTitle.bundlePackage", { count: packageList.length }),
    [selectedPackageIndex, packageList.length, packageList[selectedPackageIndex]?.name, editTitle, titleCultures.length]
  );

  const unlocalizedMeta = useMemo(
    () =>
      meta.filter(
        (x) =>
          (x.value?.value &&
            x.value.value.length > 0 &&
            !(x.value.value[0].cultureId > 0 || x.value.value[0].languageLocalizedCultureId > 0)) ||
          !x.value ||
          !x.value.value ||
          x.value.value.length === 0
      ),
    [meta]
  );

  const localizedMeta = useMemo(
    () =>
      meta.filter(
        (x) =>
          x.value?.value &&
          x.value.value.length > 0 &&
          (x.value.value[0].cultureId > 0 ||
            x.value.value[0].languageLocalizedCultureId > 0 ||
            x.value?.value[0].system?.cultureId > 0)
      ),
    [meta, selectedPackageIndex]
  );

  const synoMeta = useMemo(
    () =>
      meta && meta.length > 0
        ? meta.filter((x) => x?.field?.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos)
        : [],
    [selectedCultureIndex, meta, selectedPackageIndex]
  );
  const castMeta = useMemo(
    () =>
      meta && meta.length > 0
        ? meta.filter((x) => x?.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos)
        : [],
    [selectedCultureIndex, meta, selectedPackageIndex]
  );
  const otherMeta = useMemo(
    () =>
      meta && meta.length > 0
        ? meta.filter(
            (x) =>
              x?.field?.baseType !== BaseMetadataType.DbSynopsesMetadataValueInfos &&
              x?.field?.baseType !== BaseMetadataType.DbCastsMetadataValueInfos &&
              ((x?.value?.value &&
                x?.value?.value.length > 0 &&
                (x?.value?.value[0].languageLocalizedCultureId > 0 || x.value?.value[0].cultureId > 0)) ||
                /Localized/.test(x.value?.fieldType || ""))
          )
        : [],
    [selectedCultureIndex, meta, selectedPackageIndex]
  );
  const commonMeta = useMemo(
    () =>
      meta && meta.length > 0
        ? meta.filter(
            (x) =>
              x?.field?.baseType !== BaseMetadataType.DbSynopsesMetadataValueInfos &&
              x?.field?.baseType !== BaseMetadataType.DbCastsMetadataValueInfos &&
              !(
                (x.value?.value && x.value?.value.length > 0 && x?.value?.value[0].languageLocalizedCultureId > 0) ||
                (x.value?.value && x.value?.value.length > 0 && x.value?.value[0].cultureId > 0)
              ) &&
              !/Localized/.test(x.value?.fieldType || "")
          )
        : [],
    [selectedCultureIndex, meta, selectedPackageIndex]
  );

  const selectedCultureArtwork = useMemo(
    () =>
      artworkMeta && artworkMeta.length > 0
        ? artworkMeta.filter((x) =>
            x.metadata
              ?.find((m) => m.id === cacheService.GetMetadata().find((x) => x.tagName === "artworkCultureIds")?.id)
              ?.value.find((x) => x === selectedCultureId)
          )
        : [],
    [artworkMeta, selectedCultureId]
  );

  //------Form validation------

  const currentTitleMetadataValidation = useMemo((): BundleMetadataValidation[] => {
    if (editTitle && titleLocalizedMeta && titleLocalizedMeta.length > 0)
      return checkValidationByCountry(
        titleSelectedCulture ?? cultures.find((x) => x.id === GlobalConst.WORLD_CULTURE_ID)!,
        titleLocalizedMeta,
        selectedEpisode
      );
    else return [] as BundleMetadataValidation[];
  }, [titleLocalizedMeta, titleLocalizedMeta.length, titleSelectedCulture?.id, editTitle, selectedEpisode]);

  const currentMetadataValidation = useMemo((): BundleMetadataValidation[] => {
    if (openTab >= 0 && packageCultures[openTab] && packageCultures[openTab] && meta && meta.length > 0)
      return [
        ...checkValidationByCountry(null, unlocalizedMeta),
        ...checkValidationByCountry(packageCultures[openTab], localizedMeta, packageEpisodeNumber),
      ];
    else return [] as BundleMetadataValidation[];
  }, [openTab, meta]);

  //------Init page with acl & licence verification------

  useEffect(() => {
    if (!UserHasAcl(AclTypes.aclAssetBundlemaker) || !UserHasLicence(Licences.BundleMakerMetadata)) {
      enqueueCustomSnackbar(t("alert:error.noPermissionToAccessThisPage"), "error");
      navigate(routePaths.home.path);
    }
    if (title && title.id) {
      setObjectFullName(`${title.parentTitleName ? title.parentTitleName + " - " : ""}${title.name}`);
    }
  }, []);

  useEffect(() => {
    if (title && title.id)
      setTitleStatus(
        (GetMetadataByTagName(title, GlobalConst.TITLE_STATUS_TAGNAME)?.value as TitleStatus) ?? TitleStatus.Pending
      );
  }, [titleStatusMeta]);

  //------Main loader------

  useEffect(() => {
    if (id === -1) {
      navigate(routePaths.inventory.path);
      return;
    }
    if (id === openTitleId) return;
    dispatch(InitBundleMaker());
    dispatch(SetTitleCultures([]));
    dispatch(SetTitleLocalizedMeta([]));
    setIsLoading(true);

    // Load broadcasters and cultures
    (!broadcasters || broadcasters.length === 0) &&
      orderService.GetBroadcastersBundlemakerList().then((response) => {
        if (response && response.broadcasters) {
          dispatch(SetBroadcasters(response.broadcasters));
        }
      });
    (!cultures || cultures.length === 0) &&
      setupService.GetAllCultures().then((response) => {
        if (response && response.cultures) {
          dispatch(SetCultures(response.cultures));
        }
      });

    // Get title object data

    assetService.GetObjectLightById(id, 2).then((response) => {
      if (response && response.graph) {
        setObjectFullName(
          `${response.graph.parentTitleName ? response.graph.parentTitleName + " - " : ""}${response.graph.name}`
        );
        const bundle = getBundlesContent(response.graph);
        const artworks = getArtworksContent(response.graph);
        dispatch(SetBundleId(bundle?.id || -1));
        dispatch(SetArtworkContentId(artworks?.id || -1));
        dispatch(
          SetPackageList(
            bundle?.childObjects
              ?.filter((x) => x.classification === ObjectClass.Project && !x.isDeleted)
              .sort((a, b) => b.id - a.id) || []
          )
        );
        dispatch(
          SetArtworks(
            artworks?.childObjects
              ?.filter((x) => x.type === ObjectType.Image && !x.isDeleted)
              .sort((a, b) => b.id - a.id) || []
          )
        );

        dispatch(SetSelectedTitle(response.graph));
        // If no culture found on title or title cultures has already been load no need to get title metadata again
        (titleCultures.length > 0 || getTitleAssetCultures(response.graph, []).length === 0) && setIsLoading(false);
      } else {
        setIsLoading(false);
        enqueueCustomSnackbar(response.message || t("alert:error.loadingTitle"), "error");
      }
    });
  }, [id]);

  // When cultures are load set title cultures
  useEffect(() => {
    title?.id &&
      cultures &&
      cultures.length > 0 &&
      (titleCultures.length === 0 || openTitleId !== title?.id) &&
      dispatch(SetTitleCultures(getTitleAssetCultures(title, cultures)));
  }, [title?.id, cultures.length]);

  // When title cultures are set fetch title metadata
  useEffect(() => {
    if (titleCultures.length > 0 && title?.id && openTitleId > -1) {
      titleLocalizedMeta.length === 0
        ? fetchMeta(title.id, null, [...titleCultures.map((x) => x.id), GlobalConst.WORLD_CULTURE_ID], true)
        : fetchMeta(title.id, null, [...titleCultures.map((x) => x.id), GlobalConst.WORLD_CULTURE_ID], false);
    }
  }, [titleCultures.length, openTitleId]);

  //------Handle selected package metadata------

  useEffect(() => {
    const packageMetadata = metaMap[packageId];
    if (packageMetadata && packageMetadata.length > 0) {
      // package data already loaded
      dispatch(SetPackageCultures(getMetadataCultures(packageMetadata, cultures)));
      dispatch(SetMetadata(packageMetadata));
    } else {
      if (packageBroadcasterId && packageId) {
        fetchMeta(packageId, packageBroadcasterId, packageCultures.map((x) => x.id) || [], true);
        dispatch(SetPackageBroadcasterId(packageBroadcasterId));
      }
    }
  }, [packageId, packageList.length]);

  const fetchMeta = (
    objectId: number,
    broadcasterId: number | undefined | null,
    cultureIds: number[],
    addToCurrentMeta: boolean
  ) => {
    setIsLoading(true);
    if (objectId && (broadcasterId || objectId === title?.id) && cultureIds && cultureIds.length > 0)
      assetService
        .GetBundleMakerComputeCulturedMetadata({
          objectId: objectId,
          broadcasterId: broadcasterId,
          cultureIds: cultureIds,
          cultureLanguageOverrides: null,
        })
        .then((response) => {
          setIsLoading(false);
          if (response.success && response.metadata && response.metadata.length > 0) {
            objectId === title?.id
              ? addToCurrentMeta
                ? dispatch(
                    AddTitleMetadata({
                      metadata: response.metadata.filter((x) => x.value && isMetadataLocalized(x.value)),
                      cultureIds: cultureIds,
                    })
                  )
                : dispatch(
                    SetTitleLocalizedMeta(response.metadata.filter((x) => x.value && isMetadataLocalized(x.value)))
                  )
              : addToCurrentMeta
              ? dispatch(AddMetadata({ metadata: response.metadata, cultureIds: cultureIds }))
              : dispatch(SetMetadataMap({ packageId: objectId, metadata: response.metadata }));
          } else {
            enqueueCustomSnackbar(response.message || t("alert:error.loadingMetadata"), "error");
          }
        });
    else {
      setIsLoading(false);
      dispatch(SetMetadata([]));
    }
  };

  //------Handle modal------

  const onClose: ModalPackageCreateOrEditElementProps["onClose"] = (data) => {
    if (data.createdObjects && data.createdObjects[0]) {
      enqueueCustomSnackbar(t("alert:success.packageCreated", { count: data.createdObjects.length }), "success");
      for (var i = 0; i < data.createdObjects.length; i++) {
        dispatch(
          HandleCreatedPackage({
            selectedCultures: data.selectedCultures,
            broadcasterId: data.broadcasterId,
            createdObject: data.createdObjects[i],
          })
        );
        fetchMeta(
          data.createdObjects[i].id,
          data.broadcasterId,
          data.selectedCultures.map((x) => x.id),
          false
        );
      }
    }
  };

  const openCreatePackageModal = useCallback(() => {
    title &&
      openModal &&
      openModal<ModalPackageCreateOrEditElementProps>(ModalCreatePackage, {
        titleId: id,
        title: title,
        broadcasters: broadcasters,
        cultures: cultures,
        bundleId: bundleId,
        titleType: title?.metadata?.find((x) => x.fieldName === "titleType")?.value,
        nbEpisode: title?.metadata?.find((x) => x.fieldName === "serieSeasonEpisodeTotalNumber")?.value,
        onClose,
      });
  }, [id, title, broadcasters, cultures, bundleId]);

  //------handle user actions------

  const handleSubmitTitle = useCallback(() => {
    if (title && metadataListFilled.length > 0) {
      setIsSaving(true);
      assetService
        .UpdateMetadataFields(
          new UpdateMetadataFieldsRequest({
            objectId: title.id,
            metadata: [...metadataListFilled, ...titleLocalizedMeta.map((x) => x.value!)],
          })
        )
        .then((response) => {
          setIsSaving(false);
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.metadataUpdated"), "success");
            dispatch(SetStatusMap({ modified: false, id: title.id }));
          } else {
            enqueueCustomSnackbar(response.message || t("alert:error.updatingMetadata"), "error");
          }
        });
    }
  }, [metadataListFilled, title, titleLocalizedMeta]);

  const handleChangePackageStatus = (newStatus: ObjectStatus) => {
    dispatch(SetCurrentPackageStatus(newStatus));
  };
  const handleChangeTitleStatus = (newStatus) => {
    if ((newStatus === "Completed" || newStatus === "Validated") && !allMandatoryMetasFilled) {
      enqueueCustomSnackbar(t("alert:error.fillMandatoryMetadata", { newStatus }), "error");
      return;
    }

    if (title) {
      setIsSaving(true);
      let statusMeta =
        metadataListFilled.find((x) => x.fieldName === GlobalConst.TITLE_STATUS_TAGNAME) ??
        CreateNewDbMetadataValue(titleStatusMeta!.id, titleStatusMeta!.baseType!, titleStatusMeta!.tagName!, null);

      statusMeta = { ...statusMeta, value: newStatus };

      assetService
        .UpdateMetadataFields(
          new UpdateMetadataFieldsRequest({
            objectId: title!.id,
            metadata: [statusMeta!],
          })
        )
        .then((response) => {
          setIsSaving(false);
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.metadataUpdated"), "success");
            dispatch(SetStatusMap({ modified: false, id: title!.id }));
            setTitleStatus(newStatus);
          } else {
            enqueueCustomSnackbar(response.message || t("alert:error.updatingMetadata"), "error");
          }
        });
    }
  };

  // Handler for title global metadata
  const handleMetadataChanges = (
    isValid: boolean,
    metadata: IDbBaseMetadataValueInfos[],
    isAllMandatoryFilled: boolean,
    status: any
  ) => {
    title?.id && dispatch(SetStatusMap({ modified: true, id: title?.id }));

    setAllMandatoryMetasFilled(isAllMandatoryFilled);
    setTitleStatusMeta(status);
    setMetadataListFilled(metadata);
  };

  const handleSavePackage = (obj: DbAssetObjectInfos, returnSnack: boolean) => {
    if (!obj?.id) return;
    const packageMeta =
      packageList[selectedPackageIndex] && packageList[selectedPackageIndex].id === obj.id ? meta : metaMap[obj.id];

    setIsSaving(true);
    const newName = getGeneratedName(getMetadataCultures(packageMeta, cultures), getEpisodeMetadata(obj));
    assetService
      .ChangeStatus({
        objectId: obj.id,
        status: packageList[selectedPackageIndex].status,
        message: "Bundlemaker editing",
      })
      .then((response) => {
        if (response.success) {
          assetService.Rename({ objectId: obj.id, name: newName }).then((res) => {
            if (res.success) {
              dispatch(ChangePackageName({ name: newName, packageId: obj.id }));
              assetService
                .UpdateMetadataFields(
                  new UpdateMetadataFieldsRequest({ objectId: obj.id, metadata: packageMeta.map((x) => x.value!) })
                )
                .then((response) => {
                  setIsSaving(false);
                  if (response.success) {
                    if (packageList[selectedPackageIndex] && packageList[selectedPackageIndex].id === obj.id)
                      SetMetadata([]);
                    returnSnack && enqueueCustomSnackbar(t("alert:success.changesHasBeenSaved"), "success");
                    dispatch(SetStatusMap({ modified: false, id: obj.id }));
                  } else {
                    enqueueCustomSnackbar(response.message || t("alert:error.savingPackage"), "error");
                  }
                });
            } else {
              setIsSaving(false);
              enqueueCustomSnackbar(res.message || t("alert:error.changingPackageName"), "error");
            }
          });
        } else {
          setIsSaving(false);
          enqueueCustomSnackbar(response.message || t("alert:error.changingPackageStatus"), "error");
        }
      });
  };

  const handleSaveAll = () => {
    if (title && statusMap[title.id]) handleSubmitTitle();
    let lastSavedPackageIndex = -1;
    packageList.forEach((p, i) => {
      if (statusMap[p.id]) {
        lastSavedPackageIndex = i;
      }
    });
    for (let i = 0; i < packageList.length; i++) {
      if (
        packageList[i].id &&
        statusMap[packageList[i].id] &&
        ((i !== selectedPackageIndex && metaMap[packageList[i].id] && metaMap[packageList[i].id].length > 0) ||
          (i === selectedPackageIndex && meta.length > 0))
      ) {
        handleSavePackage(packageList[i], i === lastSavedPackageIndex);
      }
    }
  };

  const handleCancel = () => {
    if (!openModal) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.confirmCancelCurrenctEdition"),
      onConfirm: () => {
        if (editTitle && title) {
          dispatch(SetTitleCultures(getTitleAssetCultures(title, cultures)));
          dispatch(SetTitleLocalizedMeta([]));
          dispatch(SetEditTitle(false));
          dispatch(SetStatusMap({ modified: false, id: title.id }));
          fetchMeta(
            title.id,
            null,
            getTitleAssetCultures(title, cultures).map((x) => x.id),
            false
          );
        } else {
          dispatch(CancelEdition());
        }
      },
    });
  };

  const handleRemoveCulture = (e, key: number) => {
    e.stopPropagation();
    if (!openModal) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.removeCultureFromPackage"),
      onConfirm: () => {
        const assetId = packageList[selectedPackageIndex].id;
        const cultureIdToRemove = packageCultures[key]?.id;
        assetId &&
          cultureIdToRemove &&
          assetService.RemoveCulturesFromMetadata(assetId, [cultureIdToRemove]).then((response) => {
            if (response.success) {
              dispatch(RemoveCulture(key));
              enqueueCustomSnackbar(response.message ?? t("alert:success.cultureRemovedFromObject"), "success");
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.removingCulture"), "error");
            }
          });
      },
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    dispatch(SetSelectedCultureIndex(newValue));
  };

  const handleEditTitle = () => {
    selectedPackageIndex >= 0 &&
      packageList[selectedPackageIndex] &&
      dispatch(SetMetadataMap({ packageId: packageList[selectedPackageIndex].id, metadata: meta }));
    dispatch(SetEditTitle(true));
    dispatch(SetSelectedPackageIndex(-1));
    dispatch(SetSelectedCultureIndex(-1));
    dispatch(SetPackageCultures([]));
  };

  return (
    <FullTaskLayout
      sxWrapper={{ height: "100vh", maxWidth: "100vw" }}
      sxStack={{ maxWidth: "100vw", overflow: "hidden" }}
      TopBar={
        <FullTaskTopbar
          title={`${t("component:fullTaskTopbar.title.bundleMaker")} -> ${objectFullName}`}
          backRoute={`${routePaths.title.path}/${id}`}
          rightContent={
            <>
              {editTitle && (
                <Select
                  inputProps={{ "aria-label": "Without label" }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  value={titleStatus}
                  onChange={(e) => handleChangeTitleStatus(e.target.value)}
                  disabled={!UserHasAcl(AclTypes.aclBundleTitleStatusChange)}
                >
                  {titleStatusMeta?.values &&
                    titleStatusMeta?.values!.map((val) => (
                      <MenuItem value={val.value!} key={val.value!}>
                        <Typography>{val.value}</Typography>
                      </MenuItem>
                    ))}
                </Select>
              )}
              {!editTitle && packageId >= 0 && (
                <Select
                  inputProps={{ "aria-label": "Without label" }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  value={packageList[selectedPackageIndex]?.status}
                  onChange={(e) => handleChangePackageStatus(e.target.value as ObjectStatus)}
                  disabled={!UserHasAcl(AclTypes.aclBundleTitleStatusChange)}
                >
                  {Object.keys(ObjectStatus).map((status) => (
                    <MenuItem value={status} key={status}>
                      <Typography>{status}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
              {(selectedPackageIndex !== -1 || editTitle) && (
                <Button onClick={handleCancel} variant="contained" color="error">
                  {t("common:cancel")}
                </Button>
              )}
              <Tooltip title={t("tooltip:title.saveActiveForm")}>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<SaveIcon />}
                  onClick={
                    editTitle ? handleSubmitTitle : () => handleSavePackage(packageList[selectedPackageIndex], true)
                  }
                  disabled={
                    isSaving ||
                    isLoading ||
                    (selectedPackageIndex < 0 && !editTitle) ||
                    (editTitle && title && title.id && !statusMap[title.id]) ||
                    (editTitle && title && title.id && noTouchStatuses.includes(titleStatus)) ||
                    (packageCultures.length === 0 && !editTitle) ||
                    (!editTitle &&
                    packageList[selectedPackageIndex] &&
                    packageList[selectedPackageIndex].id &&
                    !statusMap[packageList[selectedPackageIndex].id]
                      ? true
                      : false)
                  }
                >
                  {t("button:saveCurrent")}
                </Button>
              </Tooltip>
              <Stack direction="row" sx={{ position: "relative" }}>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleSaveAll}
                  disabled={
                    noTouchStatuses.includes(titleStatus) ||
                    isSaving ||
                    isLoading ||
                    !Object.keys(statusMap).reduce((acc, k) => statusMap[k] || acc, false)
                  }
                >
                  {t("button:saveAll")}
                </Button>
                {isSaving && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Stack>
            </>
          }
          onClose={() => dispatch(InitBundleMaker())}
        />
      }
      subbarHeight={87}
      Subbar={
        <BundleMakerSubbar
          displayedTitle={subBarTitle}
          fetchMeta={fetchMeta}
          selectedPackageId={
            packageList && packageList.length > 0 && selectedPackageIndex >= 0
              ? packageList[selectedPackageIndex]?.id
              : undefined
          }
          selectedBroadcasterId={packageBroadcasterId}
          handleEditTitle={handleEditTitle}
          isLoading={isLoading}
          isSaving={isSaving}
        />
      }
    >
      <LeftPanelBundleMaker isLoading={isLoading} isSaving={isSaving} openCreatePackageModal={openCreatePackageModal} />
      <FullTaskSplit
        sx={{
          scrollbarGutter: "stable",
          padding: 0,
          display: "flex",
          height: "100%",
          width: "100%",
          flexDirection: "column",
          "& .MuiContainer-root": { paddingLeft: "0px", paddingRight: "0px", maxWidth: "100%" },
        }}
        sxContainer={{ maxWidth: "100%", marginTop: "142px !important" }}
        layout={packageCultures.length > 0 ? "1:2" : "1:6"}
      >
        {isLoading ? (
          <Box
            sx={{
              position: "relative",
              top: "37vh",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Stack height={editTitle ? "72px" : "100px"} spacing={1}>
            {!editTitle && selectedPackageIndex >= 0 ? (
              packageCultures.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    position: "fixed",
                    width: isRightPanelOpen ? "calc(100% - 640px)" : "-webkit-fill-available",
                    justifyContent: "space-between",
                    zIndex: 100,
                    opacity: 1,
                    backgroundColor: theme.palette.mode === "light" ? "rgb(232 232 232)" : "rgb(42 42 54)",
                    marginTop: "100px",
                    marginRight: isRightPanelOpen ? "-24px" : "0px",
                  }}
                >
                  <Tabs
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      opacity: 1,
                      height: "64px",
                      ".MuiTabScrollButton-root": { height: "71px !important" },
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    value={openTab}
                    onChange={handleTabChange}
                  >
                    {packageCultures &&
                      meta &&
                      meta.length > 0 &&
                      packageCultures.map((culture, key) => (
                        <Tab
                          sx={{
                            width: "125px",
                            paddingTop: "0px",
                            paddingBottom: "0px",
                            textOverflow: "ellipsis",
                            position: "relative",
                          }}
                          iconPosition="end"
                          icon={<ClearIcon onClick={(e) => handleRemoveCulture(e, key)} />}
                          key={key}
                          label={
                            meta &&
                            meta.length > 0 &&
                            checkValidationByCountry(culture, meta, packageEpisodeNumber).filter(
                              (x) => x.status !== BundleMetadataValidationStatus.Ok && !x.optional
                            ).length > 0 ? (
                              <>
                                <Tooltip placement="top" title={culture?.englishName?.toString()}>
                                  <Typography sx={{ width: "60%", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {culture && culture?.englishName && culture?.englishName?.toString().length > 7
                                      ? culture?.englishName?.toString().substring(0, 7) + " ..."
                                      : culture?.englishName?.toString()}
                                  </Typography>
                                </Tooltip>
                                <RequiredIndicator
                                  sx={{ left: "5px", top: "32px", position: "absolute", zIndex: 1000 }}
                                />
                              </>
                            ) : (
                              culture?.englishName?.toString()
                            )
                          }
                          {...a11yProps(key)}
                        />
                      ))}
                  </Tabs>
                </Box>
              ) : (
                <Stack
                  sx={{ position: "relative", top: "37vh" }}
                  justifyContent="center"
                  alignItems="center"
                  textAlign="center"
                  height={"100%"}
                >
                  <Typography variant="h5" component="h2">
                    {t("page:body.linkCountryToPackage")}
                  </Typography>
                  <Typography mt={1} component="p" color="text.secondary">
                    {t("page:body.selectCountryInTopRightSelectionField")}
                  </Typography>
                </Stack>
              )
            ) : (
              !editTitle && (
                <Stack
                  sx={{ position: "relative", top: "37vh" }}
                  justifyContent="center"
                  alignItems="center"
                  textAlign="center"
                  height={"100%"}
                >
                  <Typography variant="h5" component="h2">
                    {t("page:body.configureBundlePackage")}
                  </Typography>
                  <Typography mt={1} component="p" color="text.secondary">
                    {t("page:body.selectContentInLeftPanelAndEditPackageMetadata")}
                  </Typography>
                </Stack>
              )
            )}
          </Stack>
        )}
        {!isLoading &&
          packageCultures[openTab] &&
          meta &&
          meta.length > 0 &&
          selectedPackageIndex >= 0 &&
          packageList[selectedPackageIndex] &&
          !editTitle && (
            <GenerativeFormBundleMaker
              packageId={packageId}
              currentMetadataValidation={currentMetadataValidation}
              castMeta={castMeta}
              commonMeta={commonMeta}
              otherMeta={otherMeta}
              selectedCultureArtwork={selectedCultureArtwork}
              synoMeta={synoMeta}
              epNb={packageEpisodeNumber}
            />
          )}
        {editTitle && !isLoading && (
          <EditTitle
            currentTitleMetadataValidation={currentTitleMetadataValidation}
            handleMetadataChanges={handleMetadataChanges}
          />
        )}
      </FullTaskSplit>
      {((packageCultures.length > 0 && !editTitle) || (titleCultures.length > 0 && editTitle)) && (
        <RightPanelBundleMaker
          titleLocalizedMeta={titleLocalizedMeta}
          metadata={meta}
          selectedCultureId={selectedCultureId}
        />
      )}
    </FullTaskLayout>
  );
};

export default BundleMaker;

function a11yProps(index: number) {
  return {
    id: `asset-details-panel-tab-${index}`,
    "aria-controls": `asset-details-panel-tabpanel-${index}`,
  };
}
